import { IAuthContext } from "../interfaces/IAuthContext";
import { IAppUser } from "../../models/AppUser";
import { Reducer, useReducer, useEffect, useMemo } from "react";
import { useSWRCache } from "../../../sg-core/swr/useSWRCache";
import { getAuthService, ILoginResult } from "../../../sg-auth/services/SgAuthService";

import * as Endpoint from "../../../endpoints";
import { makeSuccess, makeFailure, TResult } from "../../../sg-core/utils/Result";
import { useCookie } from "../../hooks/useCookie";
import { AUTH_COOKIE as AUTH_COOKIE_KEY, RDR_COOKIE, RETURN_HREF_COOKIE } from "../../constants/StorageKeys";
import { parseQuery } from "../../utils/query.helper";
import { default as JSCookies } from "js-cookie";
import * as AuditoinnitStorage from "@/@auditoinnit/auditoinnit/constants/StorageKeys";
import * as TyoluvatStorage from "@/@dokumentit/tyoluvat/constants/StorageKeys";
import * as HavainnotStorage from "@/@havainnot/havainnot/constants/StorageKeys";
import { EAppLanguages } from "@/@common/utils/localization";

export type AuthenticationHook = () => IAuthContext;

// Default authentication hook implementation

interface IState {
    initializing: boolean;
    initialized: boolean;
    loggedIn: boolean;
    loggingIn: boolean;
    loginError?: string;
    user?: IAppUser;
    token?: string;
    returnHref?: string;
    lang?: string;
    apiLang?: TResult<string>;
    anonymousSession: boolean;
    applicationGuid?: string | null;
}

enum EAction {
    InitializationStarted = "InitializationStarted",
    InitializationComplete = "InitializationComplete",
    AnonymousLogin = "AnonymousLogin",
    StartLogin = "StartLogin",
    SuccessLogin = "SuccessLogin",
    FailureLogin = "FailureLogin",
    Logout = "Logout",
    ChangeLanguage = "ChangeLanguage",
    FailureChangeLanguage = "FailureChangeLanguage",
}

type TAction
    = { type: EAction.Logout | EAction.StartLogin | EAction.InitializationComplete | EAction.InitializationStarted }
    | { type: EAction.FailureLogin, error: string }
    | { type: EAction.SuccessLogin, user: IAppUser, token: string; returnHref?: string; lang: string; applicationGuid?: string; apiLang?: TResult<string> }
    | { type: EAction.AnonymousLogin, token: string }
    | { type: EAction.ChangeLanguage, lang: string; apiLang: TResult<string> }
    | { type: EAction.FailureChangeLanguage, apiLang: TResult<string> };

const initialState: IState = {
    initializing: false,
    initialized: false,
    loggedIn: false,
    loggingIn: false,
    anonymousSession: false,
    applicationGuid: JSCookies.get(RDR_COOKIE) || null,
    lang: EAppLanguages.FI,
    apiLang: makeSuccess("fi")
};

const reducer: Reducer<IState, TAction> = (state, action) => {

    const set = (data: Partial<IState>) => ({ ...state, ...data });

    switch (action.type) {
        case EAction.InitializationStarted: {
            return set({ initializing: true });
        }
        case EAction.InitializationComplete: {
            return set({ initialized: true, initializing: false });
        }
        case EAction.StartLogin: {
            return set({ loggedIn: false, loggingIn: true, user: undefined, loginError: undefined });
        }
        case EAction.SuccessLogin: {
            return set({ loggedIn: true, loggingIn: false, user: action.user, token: action.token, returnHref: action.returnHref, lang: action.lang, applicationGuid: action.applicationGuid });
        }
        case EAction.FailureLogin: {
            return set({ loggedIn: false, loggingIn: false, loginError: action.error });
        }
        case EAction.AnonymousLogin: {
            return set({ loggedIn: true, loggingIn: false, token: action.token, user: undefined, initialized: true, anonymousSession: true });
        }
        case EAction.Logout: {
            return set({ loggedIn: false, loggingIn: false, loginError: undefined, user: undefined, applicationGuid: undefined });
        }
        case EAction.ChangeLanguage: {
            return set({ lang: action.lang });
        }
        default: {
            return state;
        }
    }
};


export const useAuthentication: AuthenticationHook = () => {
    const [state, dispatch] = useReducer(reducer, initialState);
    const authCookie = useCookie<string>(AUTH_COOKIE_KEY, { expires: 7, sameSite: "lax", secure: true });
    const returnHref = useCookie<string>(RETURN_HREF_COOKIE, { expires: 7, sameSite: "lax", secure: true });

    const authService = getAuthService(Endpoint.Auth.path, state.anonymousSession, []);

    const { cache } = useSWRCache();


    useEffect(() => {
        if (state.loginError) {
            authCookie.delete();
        }
    }, [state.loginError]); // eslint-disable-line


    useEffect(() => {
        const guery = parseQuery<{ lang?: string }>(window.location.search);
        const rawLang = guery.lang || state.lang;
        const apiLang = state.apiLang?.isSuccess ? state.apiLang.value : "";

        const change = async (lang: any, token: any) => {
            const result = await authService.changeSessionLanguage(lang, token);
            if (result.isSuccess) {
                dispatch({ type: EAction.ChangeLanguage, lang: lang, apiLang: makeSuccess(lang) });
                return
            };
            dispatch({ type: EAction.FailureChangeLanguage, apiLang: result });
        };

        if (!state.apiLang?.isFailure && state.token && (rawLang && rawLang !== apiLang)) {
            change(rawLang, state.token)
        }
    }, [state.token]); // eslint-disable-line


    return useMemo(() => {


        const setRdr = (applicationGuid: string) => {
            JSCookies.set(RDR_COOKIE, applicationGuid, { path: process.env.PUBLIC_URL, expires: 7, sameSite: "lax", secure: true });
        }

        const clearLocalStorage = () => {
            TyoluvatStorage.clearLocalStorage();
            AuditoinnitStorage.clearLocalStorage();
            HavainnotStorage.clearLocalStorage();
        };

        const loginWithUser = (user: IAppUser, sid: string) => {
            dispatch({ type: EAction.SuccessLogin, user, token: sid, lang: user.language });
        };


        const loginWithAnonymousToken = (sid: string) => {
            dispatch({ type: EAction.AnonymousLogin, token: sid });
        };


        type TLoginFetcher = (token: string) => Promise<TResult<ILoginResult>>;

        const handleLoginResult = async (fetcher: TLoginFetcher, args: Parameters<TLoginFetcher>): Promise<TResult<ILoginResult>> => {
            try {
                const result = await fetcher(...args);
                if (!result.isSuccess) throw result.error;
                dispatch({
                    type: EAction.SuccessLogin,
                    ...result.value,
                    lang: result.value.user.language,
                    apiLang: makeSuccess(result.value.apiLanguage)
                });
                authCookie.delete();
                authCookie.set(result.value.token);
                setRdr(result.value.applicationGuid);

                if (result.value.returnHref) {
                    returnHref.set(result.value.returnHref);
                }
                return result;
            } catch (rawError) {
                const error = (rawError as Error);
                dispatch({ type: EAction.FailureLogin, error: error.message });
                return makeFailure(error);
            };
        }

        const loginWithToken = async (token: string, isReturnToken = false) => {
            clearLocalStorage();
            return await handleLoginResult(authService.activateToken, [token]);
        };

        const loginWithCookie = async () => {
            const tokenResult = authCookie.get();
            if (!tokenResult.isSuccess) {
                return tokenResult
            };
            const token = tokenResult.value

            return await handleLoginResult(authService.fetchSessionDetail, [token]);
        };


        const loginWithUrlParam = async () => {
            const { search } = window.location;
            const regex = /^\?.*token=([A-Za-z0-9-]+)/g;
            const regexResult = regex.exec(search);

            if (regexResult === null) { return makeFailure(Error("No token in URL")) }

            const [_1, token] = regexResult; // eslint-disable-line

            // Remove token form url
            window.history.pushState("object or string", "Title", window.location.pathname);

            return await loginWithToken(token);
        };

        const changeLanguage = async (lang: any) => {
            if (!state.token) return;
            //  const result = await authService.changeSessionLanguage(lang, state.token);
            //  if (result.isSuccess) {
            //      dispatch({ type: EAction.ChangeLanguage, lang: lang });
            //  };
        };

        const logOut = async () => {
            dispatch({ type: EAction.Logout });
            cache.clear();
            authCookie.delete();
            JSCookies.remove(RDR_COOKIE);
            clearLocalStorage();
        };

        const initialize = async () => {
            if (!state.initializing && !state.initialized) {
                dispatch({ type: EAction.InitializationStarted });

                if (window.location.search.indexOf("token=") > -1) {
                    await loginWithUrlParam();
                } else {
                    await loginWithCookie();
                }

                dispatch({ type: EAction.InitializationComplete });
            }
        };

        const sessionCacheKey = [state.token, state.lang].join("-")
        const returnHrefResult = returnHref.get();

        return {
            //  logIn,
            loginWithUser,
            loginWithAnonymousToken,
            changeLanguage,
            //  loginWithCookie,
            logOut,
            loggedIn: state.loggedIn,
            loggingIn: state.loggingIn,
            loginError: state.loginError,
            initialized: state.initialized,
            user: state.user,
            token: state.token,
            lang: state.lang,
            returnHref: returnHrefResult.isSuccess ? returnHrefResult.value : undefined,
            sessionCacheKey,
            initialize,
            anonymousSession: state.anonymousSession,
            applicationGuid: state.applicationGuid
        };
    }, [state]); // eslint-disable-line
};
